import type { FunctionComponent } from 'react';
import React, { useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import isNil from 'lodash/isNil';
import findIndex from 'lodash/findIndex';
import { compact } from 'lodash';

import { FlowType } from '@jerry-serverless/types';

import { RightArrowAltIcon } from '../../../icon/right_arrow_alt_icon/RightArrowAltIcon';
import { LeftArrowAltIcon } from '../../../icon/left_arrow_alt_icon/LeftArrowAltIcon';
import { StepCategory } from '../../../../types/app_data';

import { useStepNavContext } from './StepNavContext';
import type { StepNavProps } from './type';

/**
 * Step navigation bar.
 */
const UnstyledStepNav: FunctionComponent<StepNavProps> = ({
  className,
  goNext,
  goBack,
  stepIdx,
  stepCnt,
  stepIndexInCategory,
  stepCategoryCount,
  stepCategoryName,
  appData,
  categoryName,
}) => {
  const { setStepNavData } = useStepNavContext();

  const stepTitle = useMemo(() => {
    if (isNil(stepIdx) || isNil(stepCnt) || (stepCnt === 1 && stepIdx === 0)) return '';
    if (!isNil(categoryName)) {
      return categoryName;
    }

    const garageOnlyStepCategory = compact([
      appData.flowType && [FlowType.RepairEstimate, FlowType.RepairShop].includes(appData.flowType)
        ? StepCategory.GarageOnlyLocation
        : undefined,
      StepCategory.GarageOnlyCars,
      appData.flowType && [FlowType.RepairEstimate].includes(appData.flowType)
        ? StepCategory.GarageOnlyServices
        : undefined,
      appData.flowType && [FlowType.RepairShop].includes(appData.flowType) ? StepCategory.GarageOnlyService : undefined,
      appData.flowType === FlowType.CarValue ? StepCategory.GarageOnlyDriving : undefined,
      appData.flowType === FlowType.CarRecall ? StepCategory.GarageOnlyServices : undefined,
      appData.flowType === FlowType.Maintenance ? StepCategory.GarageOnlyDriving : undefined,
      StepCategory.GarageOnlyAccount,
    ]);

    const stepCategoryIndex = findIndex(garageOnlyStepCategory, (category) => category === stepCategoryName) || 0;
    const stepCategoryNameTitle = garageOnlyStepCategory
      .map((category, index) => {
        return (
          <span
            key={category}
            style={{
              color: index === stepCategoryIndex ? 'black' : '#a0afbd',
              margin: '0 0.3rem',
            }}
          >
            {category.replace('GARAGE ONLY ', '')}
          </span>
        );
      })
      .reduce((prev, curr) => (
        <React.Fragment key={`${prev.key}-${curr.key}`}>
          {prev}/{curr}
        </React.Fragment>
      ));

    if (!appData.isOnboardingLite && stepCategoryCount && !isNil(stepIndexInCategory) && stepCategoryName) {
      return (
        <Box display="flex" alignItems="center">
          {stepCategoryNameTitle}
        </Box>
      );
    } else {
      return stepCategoryName;
    }
  }, [
    stepIdx,
    stepCnt,
    categoryName,
    appData.flowType,
    appData.isOnboardingLite,
    stepCategoryCount,
    stepIndexInCategory,
    stepCategoryName,
  ]);

  useEffect(() => {
    setStepNavData({ stepTitle });
  }, [setStepNavData, stepTitle]);

  /* Wrapping nav in `motion.div` allows us to avoid showing the same component from different steps in
    between transitions by setting opacity to 0. */
  return (
    <motion.div
      variants={{ exit: { opacity: 0 }, enter: { opacity: 0 }, center: { opacity: 1 } }}
      transition={{ opacity: 0 }}
    >
      <Box flex={1} display="flex" flexDirection="row" alignItems="center" className={className}>
        <Box className="iconContainer">
          {goBack && (
            <IconButton
              aria-label="back"
              // eslint-disable-next-line jsx-a11y/aria-props
              aria-description="Go back to previous step"
              role="navigation"
              onClick={() => {
                goBack();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  goBack();
                }
              }}
            >
              <LeftArrowAltIcon />
            </IconButton>
          )}
        </Box>

        <Box flex={1} textAlign="center" className="titleContainer stepCountTitle">
          {stepTitle}
        </Box>

        <Box className={'iconContainer'}>
          {goNext && (
            <IconButton
              aria-label="next"
              onClick={() => {
                goNext(appData);
              }}
            >
              <RightArrowAltIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export const StepNav = styled(UnstyledStepNav)`
  margin-bottom: 12px;

  .iconContainer {
    width: 36px;
    height: 36px;
  }

  .titleContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    line-height: 18px
    letter-spacing: 2px;
    font-weight: 700;
    color: #a0afbd;
  }
`;
